import React from "react";

/**
 * ElbowDiagram — shows the reference technical drawing with:
 *   • blue tiled background stripped out (transparent PNG on black card)
 *   • coloured labels (d1, s1, l1, l2, r1, α) overlaid at fixed % positions
 *     so even a non-technical user sees which measurement is which
 *   • side legend explaining every symbol in the user's language
 *
 * Overlay positions are in % of the image box so the labels scale together
 * with the picture on any screen size (mobile → desktop).
 */
export default function ElbowDiagram({ labels }) {
  const L = labels || {
    d1: "d1 — vonkajší priemer",
    s1: "s1 — hrúbka steny",
    l1: "l1 — dĺžka tangens",
    l2: "l2 — dĺžka tangens",
    r1: "r1 — polomer ohybu (stred)",
    alpha: "α — uhol ohybu",
    title: "Rozmery kolena — vysvetlivky",
    caption: "Označenia zodpovedajú stĺpcom v tabuľkách nižšie.",
  };

  return (
    <div
      className="border border-zinc-800 bg-black p-3 md:p-6"
      data-testid="elbow-diagram-schematic"
    >
      <div className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-gold-400 mb-3 md:mb-4">
        {L.title}
      </div>

      <div className="grid md:grid-cols-[220px,1fr] gap-4 md:gap-8 md:items-start">
        {/* ── Picture + overlaid labels ── */}
        <div className="relative mx-auto md:mx-0 w-full max-w-[220px]">
          <img
            src="/img/elbow-diagram.png"
            alt="Schéma kolena — prierez, 90° a 45° s vyznačením rozmerov d1, s1, l1, l2, r1, α"
            className="w-full h-auto block"
            loading="lazy"
          />
          {/* Absolute-positioned labels — positions are in % so they scale with the image */}
          <DimLabel top="14%"  left="78%" color="#dc2626">d1</DimLabel>
          <DimLabel top="18%"  left="50%" color="#22c55e">s1</DimLabel>
          <DimLabel top="45%"  left="6%"  color="#dc2626">l1</DimLabel>
          <DimLabel top="63%"  left="82%" color="#dc2626">l2</DimLabel>
          <DimLabel top="60%"  left="45%" color="#f59e0b">r1</DimLabel>
          <DimLabel top="86%"  left="56%" color="#dc2626">α</DimLabel>
        </div>

        {/* ── Legend ── */}
        <ul className="space-y-2 md:space-y-2.5 text-[12px] md:text-sm font-mono">
          <LegendRow color="#dc2626" label={L.d1} />
          <LegendRow color="#22c55e" label={L.s1} />
          <LegendRow color="#dc2626" label={L.l1} />
          <LegendRow color="#dc2626" label={L.l2} />
          <LegendRow color="#f59e0b" label={L.r1} />
          <LegendRow color="#dc2626" label={L.alpha} />
        </ul>
      </div>

      <div className="mt-3 md:mt-4 text-[10px] md:text-[11px] text-zinc-500 leading-relaxed border-t border-zinc-800 pt-2 md:pt-3">
        {L.caption}
      </div>
    </div>
  );
}

/** Small italic serif label pinned to a % position on top of the image.
 *  A subtle black text-shadow keeps it readable even where a red bracket sits behind it. */
function DimLabel({ top, left, color, children }) {
  return (
    <span
      className="absolute font-bold italic pointer-events-none select-none"
      style={{
        top,
        left,
        color,
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: "clamp(11px, 3.5vw, 15px)",
        textShadow: "0 0 3px #000, 0 0 3px #000, 0 0 3px #000",
        transform: "translate(-50%, -50%)",
      }}
    >
      {children}
    </span>
  );
}

function LegendRow({ color, label }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        className="inline-block w-2.5 h-2.5 rounded-full border border-zinc-700 flex-shrink-0 mt-1.5"
        style={{ background: color }}
      />
      <span className="text-zinc-200 leading-snug">{label}</span>
    </li>
  );
}
